import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowUpRight, BookOpen, Radio, Search, Sparkles, UserRoundPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fetchArchiveProfiles, type ArchiveProfile } from "../api/archive";
import FooterNav from "../components/FooterNav";

export default function Archive() {
    const navigate = useNavigate();
    const [profiles, setProfiles] = useState<ArchiveProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");

    useEffect(() => {
        let alive = true;
        fetchArchiveProfiles()
            .then((data) => alive && setProfiles(data))
            .catch(() => alive && setProfiles([]))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return profiles;
        return profiles.filter((profile) =>
            [profile.name, profile.discipline, profile.title, ...profile.themes]
                .some((value) => String(value || "").toLowerCase().includes(q))
        );
    }, [profiles, query]);

    return (
        <div className="bs-archive-page bs-page relative min-h-screen overflow-hidden text-white">
            <div className="bs-archive-haze" aria-hidden="true" />
            <main className="relative z-10 mx-auto w-full max-w-6xl px-4 pb-32 pt-4 sm:px-7 sm:pt-7">
                <header className="bs-archive-topbar">
                    <button type="button" onClick={() => navigate("/home")} className="bs-archive-icon-button" aria-label="Back to feed"><ArrowLeft size={17} /></button>
                    <button type="button" onClick={() => navigate("/home")} className="bs-archive-wordmark" aria-label="Blackspace home"><span>Black</span><strong>space</strong><i /></button>
                    <span className="bs-archive-live"><Radio size={13} /> preserved</span>
                </header>

                <section className="bs-archive-hero">
                    <div className="min-w-0">
                        <p className="bs-archive-kicker"><BookOpen size={13} /> The Archive / 01</p>
                        <h1>Minds leave<br /><em>signals.</em></h1>
                        <p>Read the people whose work still moves through science, culture, strategy, and thought. No account is required.</p>
                    </div>
                    <div className="bs-archive-index">
                        <span>{String(profiles.length).padStart(2, "0")}</span>
                        <p>preserved<br />identities</p>
                    </div>
                </section>

                <section className="bs-archive-toolbar" aria-label="Archive search">
                    <Search size={17} />
                    <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search names, fields, or signals" aria-label="Search archive" />
                    <span>{String(filtered.length).padStart(2, "0")} found</span>
                </section>

                {loading ? (
                    <div className="bs-archive-grid" aria-label="Loading archive">
                        {Array.from({ length: 6 }).map((_, index) => <div key={index} className="bs-archive-skeleton" />)}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="bs-archive-empty"><Sparkles size={25} /><strong>No signal found</strong><span>Try another name, discipline, or theme.</span></div>
                ) : (
                    <div className="bs-archive-grid">
                        {filtered.map((profile, index) => (
                            <button key={profile.slug} type="button" onClick={() => navigate(`/archive/${profile.slug}`)} className="bs-archive-card">
                                <div className="bs-archive-card-top">
                                    <span className="bs-archive-order">{String(index + 1).padStart(2, "0")}</span>
                                    <span className={`bs-archive-avatar bg-gradient-to-br ${profile.portraitTone}`}>{profile.initials}</span>
                                    <ArrowUpRight size={17} className="bs-archive-open" />
                                </div>
                                <div className="bs-archive-card-copy">
                                    <p>{profile.discipline} / {profile.era}</p>
                                    <h2>{profile.name}</h2>
                                    <span>{profile.title}</span>
                                </div>
                                <div className="bs-archive-card-foot">
                                    <div>{profile.themes.slice(0, 2).map((theme) => <span key={theme}>{theme}</span>)}</div>
                                    <small><UserRoundPlus size={12} /> {profile.followerCount}</small>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </main>
            <footer className="bs-archive-footer fixed inset-x-0 bottom-0 z-30"><FooterNav /></footer>
        </div>
    );
}
